/**
 * ⑦ AUDIT & ASSURANCE — 5-LAYER DRILL-DOWN
 * UC-13: AuditorPortal, UC-14: RAGASEvaluation
 */
import { useState, useEffect } from "react";
import { C, api, post, patch, Btn, Card, Label, Val, Input, Badge, Err, MetricBar, Loader,
         Breadcrumb, DrillRow, DetailPanel, TabBar, HistoryTab, EmptyState, KPICard, LinkedEntity } from "./shared";

// ═══ UC-13: AUDITOR PORTAL ═══
export function AuditorPortal() {
  const [chain,setChain]=useState(null); const [events,setEvents]=useState([]); const [loading,setLoading]=useState(false);
  const [view,setView]=useState("summary"); const [sel,setSel]=useState(null); const [resDet,setResDet]=useState(null); const [tab,setTab]=useState("Overview");
  const load=async()=>{setLoading(true);try{setChain(await api("/events/verify-chain"));setEvents((await api("/audit/trail")).entries||[]);}catch(e){}setLoading(false);};
  useEffect(()=>{load();},[]);
  const drillEvent=async(ev)=>{setSel(ev);setView("detail");setTab("Overview");setResDet(null);
    if(ev.aggregate_id||ev.resource_id)try{const resType=ev.aggregate_type||"claims";const resId=ev.aggregate_id||ev.resource_id;
      if(resType.includes("claim"))setResDet(await api(`/claims/${resId}`));
      else if(resType.includes("evidence"))setResDet(await api(`/evidence/${resId}`));
      else setResDet(await api(`/events/by-aggregate/${resId}`));}catch(e){}};

  return (<div data-testid="auditor-portal-panel">
    <Breadcrumb path={[{label:"Audit",onClick:()=>{setView("summary");setSel(null);}},
      ...(view==="events"?[{label:`${events.length} Events`}]:[]),
      ...(view==="detail"&&sel?[{label:"Events",onClick:()=>{setView("events");setSel(null);}},{label:sel.event_id||sel.event_type}]:[])]}/>

    {view==="summary"&&chain&&<><div style={{display:"flex",gap:8,marginBottom:12}}>
      <KPICard label="Chain Status" value={chain.valid?"VALID":"BROKEN"} color={chain.valid?C.emerald:C.red}/>
      <KPICard label="Events" value={chain.total_events||0} color={C.blue} onClick={()=>setView("events")} sub="Click to inspect"/>
      <KPICard label="Verified" value={chain.verified||0} color={C.emerald}/></div>
      {chain.chain_head&&<div style={{fontSize:10,color:C.dim,fontFamily:"monospace",marginBottom:8}}>Chain head: {chain.chain_head}</div>}
      <Btn onClick={()=>setView("events")} color={C.emerald}>Inspect Event Trail</Btn></>}

    {view==="events"&&<><Label>Audit Trail ({events.length} events) — click for detail</Label>
      {events.map((ev,i)=>(<DrillRow key={i} onClick={()=>drillEvent(ev)} active={sel?.event_id===ev.event_id}
        cols={[<span style={{color:C.cyan,fontWeight:600}}>{ev.event_type}</span>,ev.aggregate_type||"—",
          <span style={{fontFamily:"monospace",fontSize:9}}>{(ev.chain_hash||"—").slice(0,12)}...</span>,
          <span style={{fontSize:9,color:C.muted}}>{ev.timestamp||ev.created_at||"—"}</span>]}/>))}
      {events.length===0&&<EmptyState message="No events recorded yet"/>}</>}

    {view==="detail"&&sel&&<DetailPanel visible onClose={()=>{setView("events");setSel(null);}} title={sel.event_type} color={C.emerald}>
      <TabBar tabs={["Overview","Payload","Resource","Provenance"]} active={tab} onChange={setTab}/>
      {tab==="Overview"&&<div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
          <div><Label>Type</Label><Val>{sel.event_type}</Val></div>
          <div><Label>Aggregate</Label><Val>{sel.aggregate_type}</Val></div>
          <div><Label>Sequence</Label><Val>{sel.sequence_number||sel.seq||"—"}</Val></div></div>
        <Label>Hash</Label><div style={{fontFamily:"monospace",fontSize:10,color:C.cyan,marginBottom:4}}>{sel.chain_hash||"—"}</div>
        <Label>Previous Hash</Label><div style={{fontFamily:"monospace",fontSize:10,color:C.dim}}>{sel.prev_hash||"GENESIS"}</div></div>}
      {tab==="Payload"&&<div><Label>Event Payload</Label>
        <pre style={{fontSize:10,padding:10,background:C.card,borderRadius:6,color:C.text,overflow:"auto",maxHeight:300}}>
          {JSON.stringify(sel.payload||sel,null,2)}</pre></div>}
      {tab==="Resource"&&<div>{resDet?<>
        <Label>Resource: {resDet.name||resDet.claim_text?.slice(0,40)||sel.aggregate_id}</Label>
        <div style={{fontSize:11,lineHeight:1.5,marginBottom:8}}>{resDet.claim_text||resDet.findings||resDet.name||"—"}</div>
        {resDet.status&&<Badge text={resDet.status} color={resDet.status==="approved"?C.emerald:C.amber}/>}
        </>:<div style={{color:C.muted,fontSize:11}}>Resource detail not available — may have been deleted or archived.</div>}</div>}
      {tab==="Provenance"&&<div><Label>PROV-O Lineage</Label>
        <div style={{fontSize:10,color:C.muted,marginBottom:8}}>Full provenance chain for this event's resource.</div>
        <Btn onClick={async()=>{try{const lin=await api(`/lineage/chain/ent_s`);alert(JSON.stringify(lin,null,2));}catch(e){alert("Lineage not available");}}} color={C.purple} small>Load Provenance Graph</Btn></div>}
    </DetailPanel>}
  </div>);
}

// ═══ UC-14: RAGAS EVAL ═══
export function RAGASEvaluation() {
  const [ev,setEv]=useState(null); const [loading,setLoading]=useState(false);
  const [detail,setDetail]=useState(null); const [tab,setTab]=useState("Overview");
  const run=async()=>{setLoading(true);try{setEv(await post("/eval/run",{}));}catch(e){}setLoading(false);};
  const load=async()=>{try{setEv(await api("/eval/latest"));}catch(e){}};
  useEffect(()=>{load();},[]);

  return (<div data-testid="ragas-evaluation-panel">
    {!detail&&<><div style={{display:"flex",gap:8,marginBottom:12}}>
      <KPICard label="Faithfulness" value={ev?`${Math.round((ev.faithfulness||0)*100)}%`:"—"} color={C.emerald} onClick={()=>setDetail("faithfulness")} sub="Click for breakdown"/>
      <KPICard label="Pass Rate" value={ev?`${ev.pass_rate||0}%`:"—"} color={C.blue}/>
      <KPICard label="Golden Entries" value={ev?.total||800} color={C.cyan}/></div>
      {ev&&<>{["faithfulness","context_precision","answer_relevancy"].map(m=>ev[m]!=null&&<MetricBar key={m} label={m.replace(/_/g," ")} val={ev[m]}/>)}</>}
      <Btn onClick={run} color={C.teal} disabled={loading}>{loading?"Running...":"Run Evaluation"}</Btn></>}

    {detail&&<><Breadcrumb path={[{label:"RAGAS",onClick:()=>setDetail(null)},{label:detail}]}/>
      <DetailPanel visible onClose={()=>setDetail(null)} title={`${detail} Breakdown`} color={C.emerald}>
        <TabBar tabs={["Overview","Per-Framework","Failed Entries"]} active={tab} onChange={setTab}/>
        {tab==="Overview"&&<div><MetricBar label={detail} val={ev?.[detail]||0}/>
          <div style={{fontSize:11,color:C.muted}}>Measured against {ev?.total||800} golden entries across {ev?.details?"8":"—"} frameworks and {ev?.details?"10":"—"} ESRS topics.</div></div>}
        {tab==="Per-Framework"&&<div>{ev?.details&&typeof ev.details==="string"?
          Object.entries(JSON.parse(ev.details).breakdown||{}).map(([fw,score],i)=>(
            <div key={i} style={{marginBottom:6}}><MetricBar label={fw} val={score}/></div>))
          :<div style={{color:C.muted,fontSize:11}}>Breakdown not available — run a full evaluation first.</div>}</div>}
        {tab==="Failed Entries"&&<div style={{color:C.muted,fontSize:11}}>Individual failed entries available after running a full eval. Each entry shows: golden claim, generated claim, and which checks failed.</div>}
      </DetailPanel></>}
  </div>);
}
